/**
 * Fold helpers shared by the per-widget `foldData` implementations (see `normalize-widget-args.ts`
 * for the scaffolding they plug into). Extracted when the image tools needed the exact same
 * alignment and first-defined logic text already had — a second copy would have been the third
 * place `'left' → 'L'` was spelled out.
 */

/**
 * Keys models reach for instead of the schema's `horizontalAlign`, in the order they're tried.
 * Exported so each widget's flat-synonym strip list can include them without re-listing.
 */
export const ALIGN_SYNONYM_KEYS = ['align', 'textAlign', 'alignment'] as const;

/**
 * Everyday alignment words → the persisted `'L' | 'M' | 'R'` codes. The system prompt already tells
 * the model to use schema field names verbatim, but "left"/"center"/"right" is what CSS, every
 * other builder, and the model's own priors call these — cheaper to accept than to keep losing
 * calls to it. Keys are compared with case/separators normalized away, so `"flex-start"`,
 * `"FLEX_START"` and `"flexStart"` all land on the same entry.
 */
const ALIGN_ALIASES: Readonly<Record<string, 'L' | 'M' | 'R'>> = {
    l: 'L',
    left: 'L',
    start: 'L',
    flexstart: 'L',
    m: 'M',
    c: 'M',
    center: 'M',
    centre: 'M',
    middle: 'M',
    r: 'R',
    right: 'R',
    end: 'R',
    flexend: 'R',
};

function toHorizontalAlign(value: unknown): 'L' | 'M' | 'R' | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }

    return ALIGN_ALIASES[
        value
            .trim()
            .toLowerCase()
            .replace(/[-_\s]/g, '')
    ];
}

/** First argument that isn't `undefined` — used to pick a value across a chain of synonym keys. */
export function firstDefined(...values: unknown[]): unknown {
    return values.find((value) => value !== undefined);
}

/**
 * Resolves `horizontalAlign` from the schema key or any synonym, on either `data` or `args`, and
 * writes the normalized code back onto `data` (deleting the synonyms). An unrecognized value is
 * deliberately left in place untouched so the enum rejects it loudly — a dropped field would make
 * the call a success-reporting no-op for the part the user actually asked for.
 */
export function foldHorizontalAlign(args: Record<string, unknown>, data: Record<string, unknown>): void {
    const rawAlign = firstDefined(
        data['horizontalAlign'],
        ...ALIGN_SYNONYM_KEYS.map((key) => data[key]),
        args['horizontalAlign'],
        ...ALIGN_SYNONYM_KEYS.map((key) => args[key])
    );

    for (const key of ALIGN_SYNONYM_KEYS) {
        delete data[key];
    }

    if (rawAlign !== undefined) {
        data['horizontalAlign'] = toHorizontalAlign(rawAlign) ?? rawAlign;
    }
}

/**
 * Copies the first defined value among `synonymKeys` (checked on `data` first, then `args`) onto
 * `data[targetKey]` when that key isn't already set, then deletes every synonym from `data`.
 *
 * `isValid` picks which candidate wins, so a wrong-typed synonym never shadows a well-typed one —
 * but a refused value is still handed over when it is the only one there. The flat key it arrived
 * under is stripped right after this (see `normalizeWidgetArgs`), so dropping it here would leave
 * the schema nothing to judge: the call would answer 201 with the widget's default in place of the
 * value the caller asked for, and report nothing ignored. Handed over instead, the schema either
 * coerces it (`"60"` → `60`) or names it in the error.
 */
export function foldSynonyms(
    args: Record<string, unknown>,
    data: Record<string, unknown>,
    targetKey: string,
    synonymKeys: readonly string[],
    isValid: (value: unknown) => boolean
): void {
    if (data[targetKey] === undefined) {
        const candidates = [
            ...synonymKeys.map((key) => data[key]),
            args[targetKey],
            ...synonymKeys.map((key) => args[key]),
        ].filter((value) => value !== undefined);

        const folded = candidates.find((value) => isValid(value)) ?? candidates[0];

        if (folded !== undefined) {
            data[targetKey] = folded;
        }
    }

    for (const key of synonymKeys) {
        delete data[key];
    }
}

/**
 * `normalizeWidgetArgs`' counterpart for tools with NO `data` field — remove/reorder of a widget,
 * column or block, where every argument is a flat scalar. Clones the args object, runs `fold` on it
 * and returns it; a non-object input passes through untouched so zod reports the real problem.
 *
 * The folds inside typically call `foldSynonyms(args, args, …)`, passing the same object as both
 * `args` and `data` — that helper's contract is "lift a synonym onto `data[targetKey]`", which for a
 * flat schema is the args object itself.
 */
export function normalizeFlatArgs(raw: unknown, fold: (args: Record<string, unknown>) => void): unknown {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
        return raw;
    }

    const args = { ...(raw as Record<string, unknown>) };
    fold(args);

    return args;
}

/**
 * A value that may have arrived as JSON TEXT rather than as a structure, parsed if so.
 *
 * Not a curiosity: the drawer's fallback path recovers tool calls from XML the model leaked into
 * its reply text (`parse-function-calls.ts`), and there every argument is a string — a nested object
 * arrives as `'{"primary":"#f00"}'`, an array as `'[{"name":"primary",…}]'`. A fold that only
 * accepts real objects rejects those, and the model burns a round trip per guess: three attempts to
 * set one colour, observed 2026-08-25.
 *
 * Unparsable text is returned untouched so the schema, not this helper, is what reports it.
 */
export function parseIfJsonText(value: unknown): unknown {
    if (typeof value !== 'string') {
        return value;
    }

    const trimmed = value.trim();

    // Only what could be an object or an array. Without this every ordinary string value would go
    // through `JSON.parse`, and `"123"` would quietly become a number.
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return value;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
}

export const isString = (value: unknown): boolean => typeof value === 'string';
export const isNumber = (value: unknown): boolean => typeof value === 'number';
export const isBoolean = (value: unknown): boolean => typeof value === 'boolean';
